import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import { FISCAL_YEAR_END_PATTERN } from "@/lib/fiscalProfileEditorMappers";
import type { IvaFrequency } from "@/services/fiscalService";

/** i18n key suffix per IVA frequency. Exhaustive by type: adding a frequency
 *  to IvaFrequency without a key here is a compile error, not a missing line. */
const IVA_FREQUENCY_MESSAGE_KEYS: Record<IvaFrequency, string> = {
  monthly: "ivaMonthly",
  minimum: "ivaMinimum",
  annual: "ivaAnnual",
  bimonthly: "ivaBimonthly",
};

function ivaMessageKey(frequency: IvaFrequency | ""): string | undefined {
  if (frequency === "") return undefined;
  return Object.prototype.hasOwnProperty.call(
    IVA_FREQUENCY_MESSAGE_KEYS,
    frequency,
  )
    ? IVA_FREQUENCY_MESSAGE_KEYS[frequency]
    : undefined;
}

export interface FiscalProfileSummaryLine {
  id: string;
  /** i18n key suffix under `settings.fiscalProfile.summary.*` */
  messageKey: string;
  params?: Record<string, string | number>;
}

export function buildFiscalProfileSummaryLines(
  values: FiscalProfileEditorValues,
  options: { hideAgendaFields?: boolean }
): { lines: FiscalProfileSummaryLine[] } {
  const lines: FiscalProfileSummaryLine[] = [];

  if (!options.hideAgendaFields && values.taxes.includes("IVA")) {
    // Every IvaFrequency must be mapped: ivaFrequencyForRegime yields "annual"
    // for no_cede and "bimonthly" for servicios_personales, and a missing entry
    // drops the IVA line from the screen tenants use to verify their regime.
    const messageKey = ivaMessageKey(values.ivaFrequency);
    if (messageKey != null) {
      lines.push({ id: "iva", messageKey });
    }
  }

  // Same pattern the editor validates against, so the summary can never render
  // a month/day the editor would have rejected (e.g. "13-45" from an import).
  const m = FISCAL_YEAR_END_PATTERN.exec(values.fiscalYearEnd.trim());
  if (m) {
    lines.push({
      id: "fiscalYearEnd",
      messageKey: "fiscalYearEnd",
      params: { month: m[1], day: m[2] },
    });
  }

  if (
    !options.hideAgendaFields &&
    values.taxes.includes("IRAE") &&
    values.isExportingServices &&
    values.hasExoneration
  ) {
    lines.push({
      id: "exonerationHint",
      messageKey: "mayApplyExoneration",
    });
  }

  return { lines };
}

/** Resolve translated strings for summary (call from component with useTranslation). */
export function formatFiscalProfileSummaryLine(
  line: FiscalProfileSummaryLine,
  t: (key: string, p?: Record<string, string | number>) => string
): string {
  const key = `settings.fiscalProfile.summary.${line.messageKey}`;
  return line.params != null ? t(key, line.params) : t(key);
}
